import React, { useState, useRef, useEffect } from 'react';
import { styled } from 'styled-components';
import { Button, FlexBoxHorizontal, FlexBoxVertical, FlexItem, FlexBox, Label, TranslateTypography, TextField } from '../../atoms';
import { LuImagePlus } from 'react-icons/lu';
import CloseIcon from '@mui/icons-material/Close';

export const ImgUpload = ({ data, changeImg }) => {
  const [imgSrc, setImgSrc] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const imgRef = useRef(null);

  useEffect(() => {
    if (data?.img) {
      // 캐시 방지 쿼리 파라미터 추가
      setImgSrc(`${data.img}?timestamp=${new Date().getTime()}`);
    } 
    handleDeleteImage();
  }, [data]); // data 변경 감지

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewImage(e.target.result);
      };
      reader.readAsDataURL(file);
      // 파일이 선택되면 자동으로 업로드
      await handleUpload(file);
    }
  };

  const handleUpload = async (file = selectedFile) => {
    const uploadType = !!data['color_cd'] ? 'color' : 'style';
  
    const formData = new FormData();

    // 파일 추가
    formData.append('file', file);

    // 메타데이터 추가
    formData.append('uploadType', uploadType);
    formData.append('part_cd', data['part_cd']);
    formData.append('color_cd', data['color_cd']);
  
    try {
      const response = await fetch('/api/style-color-img/s3-upload', {
        method: 'POST',
        body: formData,
      });
      const result = await response.text();
  
      // 업로드 성공 시 이미지 초기화
      handleDeleteImage();
  
      const newImgSrc = `${data.img}?timestamp=${new Date().getTime()}`;
      setImgSrc(newImgSrc);

      // 상위 컴포넌트에 업데이트된 imgSrc 전달
      if (changeImg) {
        changeImg(newImgSrc);
      }
    } catch (error) {
      console.error('업로드 오류:', error);
      alert('파일 업로드에 실패했습니다.');
    }
  };

  const fileInputRef = React.useRef();

  const handleClick = () => {
    // if (!data || !data['part_cd'] || !data['color_cd']) {
    //   alert('이미지를 업로드할 품번_컬러를 선택하세요.');
    //   return;
    // }
    fileInputRef.current.click();
  };

  // 이미지 삭제 핸들러
  const handleDeleteImage = () => {
    setSelectedFile(null);
    setPreviewImage(null);

    // 파일 입력 필드 값 초기화
    if (fileInputRef.current) {
      fileInputRef.current.value = ''; // 동일한 파일을 다시 선택할 수 있도록 초기화
    }
  };

   // 드래그 앤 드롭 및 붙여넣기 핸들러
   const handleDrop = async (event) => {
    if (!data) {
      alert('이미지를 업로드할 품번/컬러를 선택하세요.');
      handleDeleteImage();
      return;
    } else {
      if (data['brd_cd'] === 'I') {
        if (data['color_cd'] === null) {
          alert('이미지를 업로드할 컬러를 선택하세요.');
          handleDeleteImage();
          return;
        }
      }
    }
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewImage(e.target.result);
      };
      reader.readAsDataURL(file);
      // 파일이 드롭되면 자동으로 업로드
      await handleUpload(file);
    }
  };

  const handleDragOver = (event) => {
    // if (!data) {
    //   alert('이미지를 업로드할 품번/컬러를 선택하세요.');
    //   handleDeleteImage();
    //   return;
    // }
    event.preventDefault();
  };

  const handlePaste = async (event) => {
    if (!data) {
      alert('이미지를 업로드할 품번/컬러를 선택하세요.');
      handleDeleteImage();
      return;
    } else {
      if (data['brd_cd'] === 'I') {
        if (data['color_cd'] === null) {
          alert('이미지를 업로드할 컬러를 선택하세요.');
          handleDeleteImage();
          return;
        }
      }
    }
    const items = event.clipboardData.items;
    for (let item of items) {
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) {
          setSelectedFile(file);
          const reader = new FileReader();
          reader.onload = (e) => {
            setPreviewImage(e.target.result);
          };
          reader.readAsDataURL(file);
          // 파일이 붙여넣기되면 자동으로 업로드
          await handleUpload(file);
        }
      }
    }
  };

  return (
    <FlexBoxVertical style={{height: 'auto', borderRadius: '0px'}}>
      <p style={{fontSize: '15px', fontWeight: 'bold', margin: '0', color: '#6161e6'}}>
        {!!data ? !!data['color_cd'] ? `${data['part_cd']}_${data['color_cd']}` : `${data['part_cd']}` : ' '}
      </p>
      {/* 업로드 박스 */}
      <UploadBox
        $hasPreview={!!previewImage} // 프리뷰 이미지 여부에 따라 스타일 적용
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onPaste={handlePaste}
      >
        <FlexBoxVertical>
          {previewImage ? (
            <ImageContainer>
              <img
                src={previewImage}
                alt="미리보기"
                className="preview-image"
              />
              {/* X 버튼 */}
              <CloseIcon
                onClick={handleDeleteImage}
                className="delete-icon"
              />
            </ImageContainer>
          ) : (
            <>
              <LuImagePlus style={{ width: '20%', height: '20%', color: 'rgb(218, 222, 224)' }} />
              <TranslateTypography variant="content1" style={{ color: 'hsl(0, 0%, 40%)', margin: '5px' }}>
                드래그하거나 Ctrl + V를 입력하여
              </TranslateTypography>
              <TranslateTypography variant="content1" style={{ color: 'hsl(0, 0%, 40%)' }}>
                이미지를 붙여넣으세요
              </TranslateTypography>
            </>
          )}
        </FlexBoxVertical>
      </UploadBox>

      {/* 파일 선택 및 업로드 버튼 */}
      <FlexBoxHorizontal style={{ height: 'auto', padding: '10px 0' }}>
        <input
          type="file"
          onChange={handleFileChange}
          ref={fileInputRef}
          style={{ display: 'none' }}
        />
      </FlexBoxHorizontal>
    </FlexBoxVertical>

  );
};


// UploadBox 스타일 컴포넌트
const UploadBox = styled(FlexBox)`
  margin: 10px 20px 0 20px;
  border: 2px dashed rgb(218, 222, 224);
  width: 90%;
  height: 500px; /* 고정 높이 설정 */
  border-radius: 10px;
  transition: background-color 0.3s ease;
  /* 프리뷰 이미지가 없을 때만 호버 배경색 변경 */
  &:hover {
    background-color: ${({ $hasPreview }) => ($hasPreview ? 'transparent' : 'rgba(218, 222, 224, 0.2)')};
  }
`;

// ImageContainer 스타일 컴포넌트
const ImageContainer = styled.div`
  position: relative;
  width: fit-content;
  height: fit-content;
  overflow: hidden;
  max-height: 260px; /* 최대 높이 설정 */

  .preview-image {
    width:100%;
    height: 100%;
    object-fit: cover;
    transition: filter 0.3s ease;
    margin: 5px;
  }

  .delete-icon {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: white;
    cursor: pointer;
    display: none;
    background-color: rgba(0, 0, 0, 0.5);
    border-radius: 50%;
    padding: 5px;
  }

  &:hover .preview-image {
    filter: brightness(50%);
  }

  &:hover .delete-icon {
    display: block;
  }
`;


